import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import WelcomeHero from '../components/GetStarted/WelcomeHero';
import SelectImagesPanel from '../components/GetStarted/SelectImagesPanel';
import GeneratedGifPanel from '../components/GetStarted/GeneratedGifPanel';
import { gifService } from '../services/gifMakerApi';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useImagesContext } from '../context/ImagesContext';
import CardSkeleton from '../components/Skeletons/CardSkeleton';
import GifCard from '../components/GetStarted/GifCard';
import axiosInstance from 'api/axios';
import endpoints from 'features/gif-maker/gifMakerEndPoints';
import Questions from 'features/bot-builder/components/Common/Questions';
import { Apps } from 'constants/global';

export const GetStartedPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [generatedGifUrl, setGeneratedGifUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const queryClient = useQueryClient();
  const location = useLocation();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const handleImageFromUrl = async () => {
      const searchParams = new URLSearchParams(location.search);
      const imageId = searchParams.get('imageId');
      const prompt = searchParams.get('prompt');
      if (imageId && prompt) {
        setCurrentStep(2);
        try {
          setIsGenerating(true);

          // First, make the initial API call to start the generation
          const response = await axiosInstance.public.post(
            endpoints.GenerateGif,
            {
              image_url: imageId,
              prompt: prompt,
            }
          );

          if (!response) {
            throw new Error('Failed to start GIF generation');
          }

          const taskId = response.data.task_id;

          // Create WebSocket connection
          const ws = new WebSocket(
            `wss://api-fast.kreaitor.ai/api/v1/img-to-gif/ws/${taskId}`
          );

          ws.onopen = () => {
            console.log('WebSocket connection established');
          };

          ws.onmessage = (event) => {
            console.log('Raw message received:', event.data);
            try {
              const data = JSON.parse(event.data);
              console.log('Parsed message:', data);

              if (data.status === 'Completed' && data.gif_url) {
                setGeneratedGifUrl(data.gif_url);
                setIsGenerating(false);
                ws.close();
              }
            } catch (error) {
              console.error('Error parsing WebSocket message:', error);
            }
          };

          ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            ws.close();
          };

          ws.onclose = (event) => {
            console.log(
              'WebSocket connection closed:',
              event.code,
              event.reason
            );
          };
        } catch (error) {
          console.error('Error generating GIF from URL:', error);
          toast.error('Failed to generate GIF');
          setIsGenerating(false);
        }
      }
    };

    handleImageFromUrl();
  }, []);

  const saveGif = useMutation({
    mutationKey: ['save-generated-gif'],
    mutationFn: gifService.saveGif,
    onSuccess: () => {
      toast.success('Gif saved successfully');
      queryClient.invalidateQueries({ queryKey: ['user-gifs'] });
    },
    onError: (error) => {
      toast.error('Failed to save gif');
      console.error(error);
    },
  });

  const handleSave = async (title: string, video_url: string) => {
    try {
      await saveGif.mutateAsync({ video_url, title });
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  const handleGifGenerated = (url: string) => {
    setGeneratedGifUrl(url);
    setCurrentStep(2);
  };

  const isUrlMode = location.pathname.includes('/from-url');

  return (
    <main className="min-h-screen justify-center items-center bg-background-main">
      <div className="w-full h-full  flex flex-col md:flex-row justify-center bg-gradient-light dark:bg-gradient-dark bg-no-repeat bg-cover">
        <div className="w-full md:w-[500px] md:max-w-[500px] p-4 md:p-[60px]">
          <WelcomeHero />
        </div>
        <div className="w-full md:w-3/5 md:max-w-[800px] px-4 md:px-[60px] pt-4 md:pt-[60px] pb-6 md:pb-[72px]">
          {!isUrlMode && (
            <div className="flex text-text-secondary gap-4 md:gap-8">
              <div>
                <p
                  className={`text-xs md:text-sm font-medium ${currentStep === 1 ? 'text-text-primary' : 'text-text-secondary'}`}
                >
                  STEP 1
                </p>
                <h3
                  className={`text-xl md:text-[28px] ${currentStep === 1 ? 'font-bold text-text-primary' : 'text-text-secondary'}`}
                >
                  Select Image
                </h3>
              </div>
              <div>
                <p
                  className={`text-xs md:text-sm font-medium ${currentStep === 2 ? 'text-text-primary' : 'text-text-secondary'}`}
                >
                  STEP 2
                </p>
                <h3
                  className={`text-xl md:text-[28px] ${currentStep === 2 ? 'font-bold text-text-primary' : 'text-text-secondary'}`}
                >
                  Generated GIF
                </h3>
              </div>
            </div>
          )}

          {isUrlMode || currentStep === 2 ? (
            <GeneratedGifPanel
              gifUrl={generatedGifUrl}
              handleSave={handleSave}
              isLoading={saveGif.isPending}
              isGenerating={isGenerating}
              handleSetPannel={setCurrentStep}
            />
          ) : (
            <SelectImagesPanel onGifGenerated={handleGifGenerated} />
          )}
        </div>
      </div>

      <Gifs />
    </main>
  );
};

const Gifs = () => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const { isGenerating } = useImagesContext();
  const queryClient = useQueryClient();

  // Use React Query for fetching gifs
  const { data: userGifs, isLoading } = useQuery({
    queryKey: ['user-gifs'],
    queryFn: async () => {
      const response = await gifService.getUserGifs();
      if (response.gifs.length === 0) {
        const allGifs = await gifService.getAllGifs();
        return allGifs.gifs;
      } else {
        return response.gifs;
      }
    },
    enabled: isAuthenticated, // Only fetch if user is authenticated
  });

  const deleteGif = useMutation({
    mutationKey: ['gif-delete'],
    mutationFn: gifService.deleteGifById,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-gifs'] });
      toast.success('Gif deleted successfully');
    },
  });

  const updateGif = useMutation({
    mutationKey: ['edit-gif'],
    mutationFn: gifService.updateGifById,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-gifs'] });
      toast.success('Gif edited successfully');
    },
  });

  const handleDelete = (id: number) => {
    deleteGif.mutate(id);
  };

  const handleUpdate = async (
    e: React.FormEvent,
    id: number,
    fileName: string
  ) => {
    e.preventDefault();
    try {
      await updateGif.mutateAsync({ id, fileName });
    } catch (error) {
      console.error(error);
      toast.error('Failed to update gif');
    }
  };

  // Refetch when isGenerating changes
  useEffect(() => {
    if (!isGenerating) {
      queryClient.invalidateQueries({ queryKey: ['user-gifs'] });
    }
  }, [isGenerating, queryClient]);

  if (!isAuthenticated) {
    return <Questions appType={Apps.GIF_MAKER} />;
  }

  return (
    <div className="mx-auto rounded-lg rounded-t-none p-4 md:p-16 max-w-7xl">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-0">
        <h2 className="text-[24px] md:text-[30px] font-bold mb-4 md:mb-6 text-text-primary">
          GIFs at a glance, get inspired!
        </h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {isLoading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : !userGifs?.length ? (
          <div className="col-span-3 text-center text-text-primary">
            No gifs found
          </div>
        ) : (
          userGifs &&
          userGifs.map((gif: any) => (
            <div key={gif.id}>
              <GifCard
                id={gif.id}
                title={gif.title}
                handleDelete={() => handleDelete(gif.id)}
                handleUpdate={handleUpdate}
                url={gif.url}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GetStartedPage;
